import { useEffect, useMemo } from "react"
import { usePathwrightContext } from "../pathwright/PathwrightContext"
import type useUserPreference from "../user-preferences/useUserPreference"
import type { TipId, TipStatus } from "./types"

type LegacyEntry = { key: string; raw: string; status: TipStatus }

function readLegacyValues(keys: string[]): LegacyEntry[] {
  return keys.flatMap<LegacyEntry>((key) => {
    try {
      const raw = localStorage.getItem(key)
      if (raw === null) return []
      let value: unknown = raw
      try {
        value = JSON.parse(raw)
      } catch {}
      if (value && typeof value === "object") {
        // A pending edit was never confirmed saved by the old implementation.
        if ("pending" in value && value.pending === true) return []
        value = "status" in value ? value.status : undefined
      }
      if (
        value === null ||
        value === "expanded" ||
        value === "collapsed" ||
        value === "dismissed" ||
        value === "seen"
      ) {
        return [{ key, raw, status: value }]
      }
    } catch {
      // Storage may be blocked; backend preferences still work normally.
    }
    return []
  })
}

// Multiple Tip components can read the same preference. Attempt its migration
// once per Apollo client/session, including on failure, to avoid retry loops.
const attempts = new WeakMap<object, Set<string>>()

// One-way migration only: read old values from localStorage,
// upload missing preferences, then remove the old keys.
// New user actions never write to localStorage.
export function useLegacyTipStatus(
  id: TipId | undefined,
  preference: ReturnType<typeof useUserPreference<TipStatus>>
) {
  const { me, school } = usePathwrightContext()
  const userId = me?.id
  const schoolId = school?.id
  const identity = JSON.stringify([schoolId, userId, id])

  const matchingLSEntries = useMemo(
    () =>
      id && schoolId
        ? readLegacyValues([
            ...(userId ? [`tip-status:${schoolId}:${userId}:${id}`] : []),
            `tip-status:${schoolId}:anonymous:${id}`,
            `tip-${id}`
          ])
        : [],
    [id, schoolId, userId]
  )

  const { query, setStatus, upsertState } = preference
  const userPreference = query.data?.userPreference

  // Clear old localStorage entries when a matching user preference is found.
  useEffect(() => {
    if (
      !matchingLSEntries.length ||
      !userId ||
      !schoolId ||
      query.loading ||
      query.error
    )
      return

    // If we already have a matching UserPreference, clear the old localStorage entries.
    if (userPreference) {
      // Negative IDs are optimistic. Wait for a real server record before
      // removing the only durable copy of the old preference.
      if (userPreference.id > 0) {
        for (const { key, raw } of matchingLSEntries) {
          try {
            if (localStorage.getItem(key) === raw) localStorage.removeItem(key)
          } catch {}
        }
      }
      return
    }

    // Only a successful read returning null proves that a preference is absent.
    // A user edit through this consumer takes precedence over migration.
    if (userPreference !== null || upsertState.called) return

    // Avoid duplicate attempts to migrate the same client/session.
    let attempted = attempts.get(query.client)
    if (!attempted) {
      attempted = new Set()
      attempts.set(query.client, attempted)
    }
    if (attempted.has(identity)) return
    attempted.add(identity)

    // Set the UserPreference status to the first matching localStorage entry.
    setStatus(matchingLSEntries[0].status)
  }, [
    matchingLSEntries,
    userId,
    schoolId,
    query.loading,
    query.error,
    query.client,
    userPreference,
    upsertState.called,
    identity,
    setStatus
  ])

  return {
    status:
      userPreference || !matchingLSEntries.length
        ? preference.status
        : matchingLSEntries[0].status,
    loading: query.loading && !matchingLSEntries.length
  }
}
